import { setUser } from '@smg-real-estate/browser-rum';
import { setupRumViewTracking } from '@smg-real-estate/browser-rum/vue';
import { HgSupportedLanguage, SupportedPlatforms } from '@smg-real-estate/domus';
import { StorageHelper } from '@smg-real-estate/hg-search-libs';
import { until } from '@vueuse/core';
import type { ViteSSGContext } from 'vite-ssg';
import { unref } from 'vue';
import type { RouteLocationNormalizedGeneric, RouteRecordRaw } from 'vue-router';

import auth0 from '@/auth/auth0Plugin';
import { mapAuth0User } from '@/helpers/authUtils';
import { isIS24User } from '@/helpers/userUtils';
import { DEFAULT_LANGUAGE, setLocaleBeforeRouteChange } from '@/i18n-setup';
import LayoutBlank from '@/layouts/LayoutBlank.vue';
import { isPrivateRoute, isUserAuthorizedForRoute } from '@/router/routerAuth';
import { goToLogin, isUnsupportedBrowser } from '@/router/routerGuardHelpers';
import InsertionRoutes from '@/router/routes/insertionRoutes';
import LandingPageRoutes from '@/router/routes/landingPageRoutes';
import MyAdsRoutes from '@/router/routes/myAdsRoutes';
import { TargetPlatform, getCurrentPlatform } from '@/services/envService';
import { useFeatureFlagsStore } from '@/stores/featureFlags';
import { useInsertionStore } from '@/stores/insertion';
import { useUserStore } from '@/stores/user';
import { UserGroup } from '@/types';

const storageHelper = new StorageHelper('insertion-funnel');

const UNSUPPORTED_BROWSER_PATH = 'unsupported-browser';

export const routes: RouteRecordRaw[] = [
  ...LandingPageRoutes,
  {
    path: '/:locale',
    name: 'main',
    component: LayoutBlank,
    children: [
      {
        path: 'insertion',
        component: () => import('@/layouts/LayoutInsertion.vue'),
        children: InsertionRoutes,
      },
      {
        path: 'my-ads',
        component: LayoutBlank,
        children: MyAdsRoutes,
      },
      {
        path: UNSUPPORTED_BROWSER_PATH,
        name: UNSUPPORTED_BROWSER_PATH,
        component: () => import('@/views/UnsupportedBrowserPage/UnsupportedBrowserPage.vue'),
      },
      {
        path: 'insertion/new',
        name: 'insertion-new',
        component: () => import('@/views/NewPage/NewPage.vue'),
        meta: {
          authorizedGroups: [UserGroup.CUSTOMER_SERVICE_AGENT, UserGroup.PRIVATE],
          viewName: 'Insertion New Page',
        },
      },
    ],
  },
  {
    path: '/:locale/*',
    name: 'pageNotFound',
    component: () => import('@smg-real-estate/domus/HgPageNotFound.vue'),
    props: () => ({
      theme:
        getCurrentPlatform() === TargetPlatform.HOMEGATE
          ? SupportedPlatforms.hg
          : SupportedPlatforms.is24,
      pageLayoutCssClass: 'layout-container',
    }),
  },
];

export const installGuards = (ctx: ViteSSGContext) => {
  const { router, isClient } = ctx;
  const userStore = useUserStore();
  router.beforeEach((targetRoute, _sourceRoute, next) => {
    if (isClient && isUnsupportedBrowser() && targetRoute.name !== UNSUPPORTED_BROWSER_PATH) {
      const locale = getLocale(targetRoute);
      return next(`/${locale}/${UNSUPPORTED_BROWSER_PATH}`);
    }

    return next();
  });

  // authentication
  if (isClient) {
    // Initialize auth0 user
    router.beforeEach(async (targetRoute, _from, next) => {
      // If the PKCE flow has not finished, wait for the session check
      if (unref(auth0.isLoading)) {
        await until(auth0.isLoading).toBe(false);
      }

      if (!unref(auth0.isAuthenticated)) {
        // If the user is not authenticated, check if they have a guest token
        const guestToken =
          targetRoute.query.token?.toString() || storageHelper.getLocalStorageItem('guestToken');
        if (guestToken) {
          storageHelper.setLocalStorageItem('guestToken', guestToken);
          await userStore.setGuestUser(guestToken);
          return next();
        }

        // If the user is not authenticated and does not have a guest token, skip to the next guard
        userStore.isUserLoaded = true;
        return next();
      }

      const authenticatedUser = mapAuth0User(auth0.user.value);

      if (!authenticatedUser) {
        userStore.isUserLoaded = true;
        return next();
      }

      userStore.setAuthenticatedUser(authenticatedUser, true);
      setUser({ id: authenticatedUser.userId, groups: authenticatedUser.groups });

      return next();
    });

    router.beforeEach((to, _from, next) => {
      const routeName = to.name;
      // The landing page experiment is Homegate-only
      const shouldFetchLandingPageFlags =
        typeof routeName === 'string' &&
        routeName.startsWith('landingpage-') &&
        routeName !== 'landingpage-iframe' &&
        !isIS24User();

      if (shouldFetchLandingPageFlags) {
        void useFeatureFlagsStore().fetchFlagSets();
      }
      return next();
    });

    // Protect private routes
    router.beforeEach(async (to, _from, next) => {
      if (!isPrivateRoute(to)) {
        return next();
      }

      if (to.name === 'insertion-new') {
        return next();
      }

      if (!userStore.hasUser) {
        const locale = getLocale(to);
        goToLogin(to, locale);
        return next(false);
      }

      if (!isUserAuthorizedForRoute(userStore.userGroups, to)) {
        return next({
          path: `/${to.params.locale}/my-ads`,
          replace: true,
        });
      }

      return next();
    });

    router.beforeEach(async (to, _from, next) => {
      if (to.name !== 'bundles') {
        return next();
      }

      const insertionId = to.params.id;
      if (typeof insertionId !== 'string' || !insertionId) {
        return next();
      }

      if (!userStore.hasUser) {
        return next();
      }

      const insertionStore = useInsertionStore();
      const ownerId = resolveInsertionOwnerId(to, userStore);

      if (!insertionStore.insertion?.id || insertionStore.insertion.id !== insertionId) {
        try {
          await insertionStore.fetchInsertion({ ownerId, insertionId });
          insertionStore.captureListingSnapshot();
        } catch {
          return next();
        }
      }

      if (insertionStore.listingCategories.length === 0) {
        return next({
          name: 'details',
          params: { locale: getLocale(to), id: insertionId },
          query: to.query,
          replace: true,
        });
      }

      return next();
    });

    // This only runs when the (CCC) user pass all other navigation guards
    router.beforeResolve(async (to, from, next) => {
      if (!userStore.userGroups.includes(UserGroup.CUSTOMER_SERVICE_AGENT)) return next();

      const IMPERSONATION_ROUTES = ['details', 'bundles', 'billing', 'confirmation'];
      if (!IMPERSONATION_ROUTES.includes(to.name as string)) {
        if (to.query.ownerId) {
          // Clear the ownerId from the query params if not allowed to impersonate
          return next({ path: to.path, query: {}, replace: true });
        }
        return next();
      }

      // If routed from outside the of the insertion funnel with an ownerId,
      // fetch the user type again so that it's correct for tracking and pricing
      if (!from.name && to.query.ownerId) {
        await userStore.fetchUserType(to.query.ownerId as string);
        return next();
      }

      // If there is an ownerId in the query params, forward it to the next route
      if (from.query.ownerId) {
        to.query.ownerId = from.query.ownerId as string;
      }

      return next();
    });
  }

  router.beforeEach(async (targetRoute, _sourceRoute, next) => {
    const locale = getLocale(targetRoute);
    await setLocaleBeforeRouteChange(locale);
    next();
  });

  setupRumViewTracking(router);
};

function getLocale(
  route: RouteLocationNormalizedGeneric,
  defaultLanguage: HgSupportedLanguage = DEFAULT_LANGUAGE,
): HgSupportedLanguage {
  return (route.params.locale || route?.meta?.locale || defaultLanguage) as HgSupportedLanguage;
}

function resolveInsertionOwnerId(
  route: RouteLocationNormalizedGeneric,
  userStore: ReturnType<typeof useUserStore>,
): string {
  const impersonatedOwnerId = route.query.ownerId;
  const isImpersonation =
    userStore.userGroups.includes(UserGroup.CUSTOMER_SERVICE_AGENT) &&
    typeof impersonatedOwnerId === 'string' &&
    impersonatedOwnerId !== userStore.userId;

  return isImpersonation ? impersonatedOwnerId : userStore.userId;
}
